(function () {
  const STORAGE_PREFIX = 'cf-expanded-';
  const TAB_STORAGE_PREFIX = 'cf-active-tab-';
  const expandedStateBySection = new Map();

  function storageKey(filterRoot) {
    const sectionId = filterRoot.dataset.sectionId || '';
    return STORAGE_PREFIX + sectionId;
  }

  function readExpanded(filterRoot) {
    const sectionId = filterRoot.dataset.sectionId || '';
    if (expandedStateBySection.has(sectionId)) {
      return expandedStateBySection.get(sectionId);
    }
    const stored = sessionStorage.getItem(storageKey(filterRoot));
    if (stored === 'true' || stored === 'false') {
      return stored === 'true';
    }
    return filterRoot.dataset.expanded === 'true';
  }

  function persistExpanded(filterRoot, expanded) {
    const sectionId = filterRoot.dataset.sectionId || '';
    expandedStateBySection.set(sectionId, expanded);
    sessionStorage.setItem(storageKey(filterRoot), expanded ? 'true' : 'false');
  }

  const pendingCollapseFrames = new WeakMap();

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function animateBody(body, expanded) {
    const pending = pendingCollapseFrames.get(body);
    if (pending) {
      cancelAnimationFrame(pending);
      pendingCollapseFrames.delete(body);
    }

    if (expanded) {
      body.style.overflow = 'hidden';
      body.style.opacity = '1';
      body.style.maxHeight = body.scrollHeight + 'px';
      const onEnd = (event) => {
        if (event.target !== body || event.propertyName !== 'max-height') return;
        body.removeEventListener('transitionend', onEnd);
        const root = body.closest('[data-collection-filters]');
        if (root && root.dataset.expanded !== 'true') return;
        body.style.maxHeight = 'none';
        body.style.overflow = '';
      };
      body.addEventListener('transitionend', onEnd);
    } else {
      body.style.overflow = 'hidden';
      body.style.maxHeight = body.scrollHeight + 'px';
      void body.offsetHeight;
      const frame = requestAnimationFrame(() => {
        pendingCollapseFrames.delete(body);
        body.style.maxHeight = '0px';
        body.style.opacity = '0';
      });
      pendingCollapseFrames.set(body, frame);
    }
  }

  function setBodyState(body, expanded, animate) {
    if (!body) return;
    body.hidden = false;
    body.setAttribute('aria-hidden', expanded ? 'false' : 'true');
    if (expanded) {
      body.removeAttribute('inert');
    } else {
      body.setAttribute('inert', '');
    }

    if (animate && !prefersReducedMotion()) {
      animateBody(body, expanded);
      return;
    }

    body.style.maxHeight = expanded ? 'none' : '0px';
    body.style.opacity = expanded ? '1' : '0';
    body.style.overflow = expanded ? '' : 'hidden';
  }

  function applyExpanded(filterRoot, expanded, animate) {
    filterRoot.dataset.expanded = expanded ? 'true' : 'false';
    filterRoot.classList.toggle('cf--collapsed', !expanded);

    const body = filterRoot.querySelector('[data-cf-body]');
    const tabs = filterRoot.querySelector('[data-cf-tabs]');
    const toggle = filterRoot.querySelector('[data-cf-toggle]');

    setBodyState(body, expanded, animate);
    if (tabs) tabs.hidden = !expanded;
    if (toggle) toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  function setExpanded(filterRoot, expanded, persist) {
    applyExpanded(filterRoot, expanded, true);
    if (persist) {
      persistExpanded(filterRoot, expanded);
    }
  }

  function tabStorageKey(filterRoot) {
    const sectionId = filterRoot.dataset.sectionId || '';
    return TAB_STORAGE_PREFIX + sectionId;
  }

  function readActiveTab(filterRoot) {
    const stored = sessionStorage.getItem(tabStorageKey(filterRoot));
    if (!stored) return null;
    if (filterRoot.querySelector('[data-cf-tab="' + stored + '"]')) {
      return stored;
    }
    return null;
  }

  function persistActiveTab(filterRoot, tabId) {
    sessionStorage.setItem(tabStorageKey(filterRoot), tabId);
  }

  function activateTab(filterRoot, tabId, persist) {
    filterRoot.querySelectorAll('[data-cf-tab]').forEach((tabElement) => {
      const isActive = tabElement.getAttribute('data-cf-tab') === tabId;
      tabElement.classList.toggle('cf__tab--active', isActive);
      tabElement.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tabElement.setAttribute('tabindex', isActive ? '0' : '-1');
    });
    filterRoot.querySelectorAll('[data-cf-panel]').forEach((panelElement) => {
      const isActive = panelElement.getAttribute('data-cf-panel') === tabId;
      panelElement.classList.toggle('cf__panel--active', isActive);
      panelElement.classList.toggle('cf__panel--hidden', !isActive);
      panelElement.hidden = !isActive;
    });
    if (persist) {
      persistActiveTab(filterRoot, tabId);
    }
  }

  // Keep page from sliding sideways; scroll only the filter row into view on Tab.
  function lockPageScrollX() {
    document.documentElement.classList.add('cf-lock-scroll-x');
    if (window.scrollX || document.documentElement.scrollLeft || document.body.scrollLeft) {
      document.documentElement.scrollLeft = 0;
      document.body.scrollLeft = 0;
      window.scrollTo(0, window.scrollY || window.pageYOffset || 0);
    }
  }

  function scrollFilterRowToElement(element) {
    if (!element) return;
    const item = element.closest('.cf-card, .cf-color-swatch, [data-cf-tab]') || element;
    const scroller = item.closest('.cf__cards, .cf__color-swatches, .cf__tabs');
    if (!scroller) return;

    const scrollerRect = scroller.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const pad = 12;

    if (itemRect.left < scrollerRect.left + pad) {
      scroller.scrollLeft -= scrollerRect.left + pad - itemRect.left;
    } else if (itemRect.right > scrollerRect.right - pad) {
      scroller.scrollLeft += itemRect.right - (scrollerRect.right - pad);
    }
  }

  function focusInFilterRow(element) {
    if (!element || typeof element.focus !== 'function') return;
    try {
      element.focus({ preventScroll: true });
    } catch (error) {
      element.focus();
    }
    scrollFilterRowToElement(element);
    lockPageScrollX();
  }

  function updateCountElement(element, count) {
    if (!element) return;
    if (count > 0) {
      element.hidden = false;
      element.textContent = '(' + count + ')';
    } else {
      element.hidden = true;
      element.textContent = '';
    }
  }

  function syncCounts(filterRoot, form) {
    let totalChecked = 0;
    form.querySelectorAll('input[type="checkbox"]:checked').forEach(() => {
      totalChecked++;
    });

    const clearButton = filterRoot.querySelector('[data-cf-clear]');
    const clearCount = filterRoot.querySelector('.cf__clear-count');
    if (clearButton) {
      clearButton.hidden = totalChecked === 0;
      clearButton.classList.toggle('cf__clear--hidden', totalChecked === 0);
      if (clearCount) clearCount.textContent = totalChecked > 0 ? ' (' + totalChecked + ')' : '';
    }

    filterRoot.querySelectorAll('[data-cf-panel]').forEach((panelElement) => {
      const filterKey = panelElement.getAttribute('data-cf-filter-key');
      if (!filterKey) return;
      let count = 0;
      panelElement.querySelectorAll('input[type="checkbox"]:checked').forEach(() => {
        count++;
      });
      filterRoot.querySelectorAll('[data-cf-tab-count="' + filterKey + '"]').forEach((countElement) => {
        updateCountElement(countElement, count);
      });
    });

    filterRoot.querySelectorAll('[data-cf-swatch-row]').forEach((swatchRow) => {
      const filterKey = swatchRow.getAttribute('data-cf-swatch-row');
      let count = 0;
      swatchRow.querySelectorAll('input[type="checkbox"]:checked').forEach(() => {
        count++;
      });
      filterRoot.querySelectorAll('[data-cf-tab-count="' + filterKey + '"]').forEach((countElement) => {
        updateCountElement(countElement, count);
      });
    });
  }

  const DRAG_SCROLL_SELECTOR = '.cf__cards, .cf__color-swatches, .cf__tabs';
  const DRAG_THRESHOLD = 5;
  let dragDocumentBound = false;
  const dragState = {
    scrollElement: null,
    active: false,
    startX: 0,
    scrollStart: 0,
    suppressClick: false,
  };

  function isHorizontallyScrollable(element) {
    return element && element.scrollWidth > element.clientWidth + 1;
  }

  function refreshDragScrollTargets(filterRoot) {
    filterRoot.querySelectorAll(DRAG_SCROLL_SELECTOR).forEach((scrollElement) => {
      scrollElement.classList.toggle('cf__scroll--draggable', isHorizontallyScrollable(scrollElement));
    });
  }

  function endDragScroll() {
    if (!dragState.scrollElement && !dragState.active) return;
    if (dragState.active) {
      dragState.suppressClick = true;
    }
    if (dragState.scrollElement) {
      dragState.scrollElement.classList.remove('cf__scroll--dragging');
    }
    dragState.scrollElement = null;
    dragState.active = false;
    document.body.classList.remove('cf-scroll-dragging');
  }

  function onDragScrollMove(event) {
    if (!dragState.scrollElement) return;
    const delta = event.clientX - dragState.startX;
    if (!dragState.active) {
      if (Math.abs(delta) < DRAG_THRESHOLD) return;
      dragState.active = true;
      dragState.scrollElement.classList.add('cf__scroll--dragging');
      document.body.classList.add('cf-scroll-dragging');
    }
    event.preventDefault();
    dragState.scrollElement.scrollLeft = dragState.scrollStart - delta;
  }

  function ensureDragScrollDocumentListeners() {
    if (dragDocumentBound) return;
    dragDocumentBound = true;
    document.addEventListener('mousemove', onDragScrollMove);
    document.addEventListener('mouseup', endDragScroll);
  }

  function initDragScroll(filterRoot) {
    ensureDragScrollDocumentListeners();
    refreshDragScrollTargets(filterRoot);

    if (filterRoot.dataset.cfDragScrollBound === 'true') return;
    filterRoot.dataset.cfDragScrollBound = 'true';

    filterRoot.addEventListener('mousedown', (event) => {
      if (event.button !== 0) return;

      // Avoid focus-scroll on click inside the horizontal card row.
      if (event.target.closest('.cf-card, .cf-color-swatch')) {
        event.preventDefault();
      }

      const scrollElement = event.target.closest(DRAG_SCROLL_SELECTOR);
      if (!scrollElement || !filterRoot.contains(scrollElement) || !isHorizontallyScrollable(scrollElement)) return;

      dragState.scrollElement = scrollElement;
      dragState.active = false;
      dragState.startX = event.clientX;
      dragState.scrollStart = scrollElement.scrollLeft;
    });

    filterRoot.addEventListener('click', (event) => {
      if (!dragState.suppressClick) return;
      dragState.suppressClick = false;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);

    filterRoot.addEventListener('dragstart', (event) => {
      if (dragState.active && event.target.closest(DRAG_SCROLL_SELECTOR)) {
        event.preventDefault();
      }
    });
  }

  function initTabKeyboard(filterRoot) {
    if (filterRoot.dataset.cfTabKeysBound === 'true') return;
    filterRoot.dataset.cfTabKeysBound = 'true';

    filterRoot.addEventListener('keydown', (event) => {
      const tabElement = event.target.closest('[data-cf-tab][role="tab"]');
      if (!tabElement || !filterRoot.contains(tabElement)) return;

      const tabs = Array.from(filterRoot.querySelectorAll('[data-cf-tab][role="tab"]'));
      const index = tabs.indexOf(tabElement);
      if (index < 0) return;

      let nextIndex = -1;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        nextIndex = (index + 1) % tabs.length;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        nextIndex = (index - 1 + tabs.length) % tabs.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = tabs.length - 1;
      } else {
        return;
      }

      event.preventDefault();
      const nextTab = tabs[nextIndex];
      activateTab(filterRoot, nextTab.getAttribute('data-cf-tab'), true);
      focusInFilterRow(nextTab);
    });
  }

  function initCollectionFilters(filterRoot) {
    if (!filterRoot) return;

    lockPageScrollX();
    initDragScroll(filterRoot);
    initTabKeyboard(filterRoot);
    applyExpanded(filterRoot, readExpanded(filterRoot));

    const savedTab = readActiveTab(filterRoot);
    if (savedTab) {
      activateTab(filterRoot, savedTab, false);
    } else {
      const activeTab = filterRoot.querySelector('[data-cf-tab].cf__tab--active, [data-cf-tab][aria-selected="true"]');
      if (activeTab) {
        activateTab(filterRoot, activeTab.getAttribute('data-cf-tab'), false);
      }
    }

    const form = filterRoot.querySelector('#CollectionFilterForm');
    if (form && !filterRoot.dataset.cfFormBound) {
      filterRoot.dataset.cfFormBound = 'true';
      form.addEventListener('change', () => {
        // Blur so theme ajax does not restore focus into the card row (side-scroll).
        if (document.activeElement && filterRoot.contains(document.activeElement)) {
          document.activeElement.blur();
        }
        persistExpanded(filterRoot, filterRoot.dataset.expanded === 'true');
        syncCounts(filterRoot, form);
      });
      syncCounts(filterRoot, form);
    } else if (form) {
      syncCounts(filterRoot, form);
    }
  }

  function initAll(container) {
    (container || document).querySelectorAll('[data-collection-filters]').forEach((filterRoot) => {
      initCollectionFilters(filterRoot);
    });
  }

  document.addEventListener('change', (event) => {
    const form = event.target.closest('#CollectionFilterForm');
    if (!form) return;
    const filterRoot = form.closest('[data-collection-filters]');
    if (!filterRoot) return;
    if (document.activeElement && filterRoot.contains(document.activeElement)) {
      document.activeElement.blur();
    }
    persistExpanded(filterRoot, filterRoot.dataset.expanded === 'true');
  }, true);

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-cf-toggle]');
    if (toggle) {
      const filterRoot = toggle.closest('[data-collection-filters]');
      if (!filterRoot) return;
      event.preventDefault();
      event.stopPropagation();
      setExpanded(filterRoot, filterRoot.dataset.expanded !== 'true', true);
      return;
    }

    const tabElement = event.target.closest('[data-cf-tab]');
    if (tabElement && tabElement.getAttribute('role') === 'tab') {
      const filterRoot = tabElement.closest('[data-collection-filters]');
      if (!filterRoot) return;
      event.preventDefault();
      activateTab(filterRoot, tabElement.getAttribute('data-cf-tab'), true);
      focusInFilterRow(tabElement);
    }
  });

  // Tab into off-screen cards/swatches: scroll the row only.
  document.addEventListener('focusin', (event) => {
    const target = event.target;
    if (!target || !target.closest || !target.closest('[data-collection-filters]')) return;
    if (!target.closest('.cf-card, .cf-color-swatch, [data-cf-tab]')) return;
    scrollFilterRowToElement(target);
    lockPageScrollX();
  }, true);

  document.addEventListener('DOMContentLoaded', () => initAll(document));

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  if (typeof jQuery !== 'undefined') {
    jQuery(document).on('cc:component:load', function (event, component, target) {
      if (component === 'collection-filters' || (component && component.trim() === 'collection-filters')) {
        initAll(target);
      }
    });

    jQuery(document).ajaxComplete(function (event, xhr, settings) {
      if (!settings || !settings.url || settings.url.indexOf('section_id=') === -1) return;
      initAll(document);
      lockPageScrollX();
    });
  }

  window.theme = window.theme || {};
  window.theme.initCollectionFilters = initAll;
})();
